import { useParams } from "react-router";
import { pageTitleForJobTrackerPath, JobTrackerPath } from "../JobTrackerRoutes";

export interface JobTrackingInternalLinkProps {
  trackerPath: JobTrackerPath;
  children: React.FC<{ targetPath: string; targetPageTitle: string }>;
}

/**
 * Helper component to render a link to a job tracking page, specified by the `trackerPath` prop. The child component
 * is passed a `targetPath` prop that contains the full path to the job tracking page, which is constructed
 * based on the current job number in the URL route params.
 *
 * This child component is also passed a `targetPageTitle` prop, which is the title of the target page.
 *
 * For example, if the current route is `/job/SO-1234/details` and `trackerPath` is `bom-hierarchy`,
 * the child component will receive a `targetPath` prop as `/job/SO-1234/bom-hierarchy`.
 *
 * If this component is used on a page that does not have a `jobNumber` route param, it will throw an error.
 *
 * @example
 * ```tsx
 * <JobTrackingInternalLink trackerPath="builds">
 *   {({ targetPath, targetPageTitle }) => (
 *     <SOSEntityLineItemHashLink
 *       fromHash={row.id}
 *       urlPath={targetPath}
 *       tooltipContent={`View on '${targetPageTitle}' page`}
 *       type="build"
 *       id={build.id}
 *       lineNumber={0}
 *       label={build.number}
 *     />
 *   )}
 * </JobTrackingInternalLink>
 * ```
 */
export const JobTrackingInternalLink: React.FC<JobTrackingInternalLinkProps> = ({
  trackerPath,
  children: ChildComponent,
}) => {
  const routeParams = useParams();
  const jobNumber = routeParams["jobNumber"];
  if (!jobNumber) throw new Error("No job number found in URL route params");
  const targetPath = `/job/${jobNumber}/${trackerPath}`;
  const targetPageTitle = pageTitleForJobTrackerPath[trackerPath];
  return <ChildComponent targetPath={targetPath} targetPageTitle={targetPageTitle} />;
};
